/**
 * Owner: Masa Bayu
 * File: render-body-layer.tsx
 * Date:Time: 2026-10-15 06:14
 * Description: Body layer · legacy / v1 tip / v2 L2 line-paint (R11.4).
 */

import type { ReactElement } from 'react';
import {
  groupNativeLinesIntoParagraphs,
  isEngineParagraphGroup,
} from './group-native-paragraphs';
import { writePageDropCapIndex } from '@qx/qx-layout-write';
import { paintDropCapFromTip, type TipTextElement } from '@qx/qx-render';
import { getRenderPipe } from '../feature-flags';
import type { AssetLibrary, TextElement } from '../extract/spsu-types';
import { DropCapFromTipFaceObject } from './dropcap-from-tip-face-object';
import { DropCapJustifiedFaceObject } from './dropcap-justified-face-object';
import { ParagraphJustifiedFaceObject } from './paragraph-justified-face-object';
import { LiveObject } from './qx-editor-page-face-objects';
import { renderBodyLayerV2 } from './render-body-layer-v2';
import {
  detectNativeDropCapPairs,
  nativeDropCapSkipIds,
} from './spsu-native-dropcap-face';

export type BodyLayerOnTextChange = (
  elementId: string,
  newContent: string,
  tipLine?: import('@qx/qx-line-paint').QXWLine,
) => void;

function toTipElement(el: TextElement): TipTextElement {
  return {
    id: el.id,
    content: el.content,
    bbox: {
      x: el.bbox.x,
      y: el.bbox.y,
      width: el.bbox.width,
      height: el.bbox.height,
    },
    style: {
      fontFamily: el.style.fontFamily,
      fontSize: el.style.fontSize,
      textAlign: el.style.textAlign,
    },
  };
}

function tipSkipIds(
  letterId: string,
  hostId: string,
  absorbIds: readonly string[],
  flowIds: readonly string[],
): Set<string> {
  const ids = new Set<string>([letterId, hostId, ...absorbIds, ...flowIds]);
  return ids;
}

/**
 * Legacy KILL_HOT path · DropCapJustified + ParagraphJustified remasure.
 * Unchanged until R10.5/R10.6 retire.
 */
function renderBodyLayerLegacy(
  bodyTexts: readonly TextElement[],
  assets: AssetLibrary,
  editable: boolean,
  onTextChange: BodyLayerOnTextChange | undefined,
): ReactElement {
  const paragraphGroups = groupNativeLinesIntoParagraphs(bodyTexts);
  const engineGroups = paragraphGroups.filter(isEngineParagraphGroup);
  const engineSkip = new Set<string>();
  for (const group of engineGroups) {
    for (const plate of group) engineSkip.add(plate.id);
  }
  const dropCapPairs = detectNativeDropCapPairs(bodyTexts);
  const dropCapByLetter = new Map(
    dropCapPairs.map((pair) => [pair.letterId, pair] as const),
  );
  const dropCapSkip = nativeDropCapSkipIds(
    dropCapPairs.filter((pair) => !engineSkip.has(pair.letterId)),
  );
  const absoluteSkip = new Set<string>([...engineSkip, ...dropCapSkip]);
  for (const pair of dropCapPairs) absoluteSkip.add(pair.letterId);

  return (
    <>
      {engineGroups.map((group, groupIndex) => {
        const dropPair = dropCapPairs.find((pair) =>
          group.some((plate) => plate.id === pair.letterId),
        );
        if (dropPair) {
          return (
            <DropCapJustifiedFaceObject
              key={`dropcap-justified-${dropPair.letterId}-${dropPair.hostId}`}
              pair={dropPair}
              pageTexts={bodyTexts}
              paragraphPlates={group}
              assets={assets}
            />
          );
        }
        return (
          <ParagraphJustifiedFaceObject
            key={`paragraph-justified-${groupIndex}-${group[0]?.id ?? groupIndex}`}
            plates={group}
            assets={assets}
          />
        );
      })}
      {bodyTexts.map((element) => {
        if (absoluteSkip.has(element.id) || dropCapByLetter.has(element.id)) {
          return null;
        }
        return (
          <LiveObject
            key={element.id}
            element={element}
            assets={assets}
            editable={editable}
            {...(onTextChange ? { onTextChange } : {})}
          />
        );
      })}
    </>
  );
}

/**
 * v1 tip path · writePageDropCapIndex → paintDropCapFromTip → shell.
 * Paragraph engine groups → LiveObject rows (tipAlign · no remasure).
 * Q1: paint null / empty tip pair → legacy DropCap for that group only.
 */
function renderBodyLayerV1(
  bodyTexts: readonly TextElement[],
  assets: AssetLibrary,
  editable: boolean,
  onTextChange: BodyLayerOnTextChange | undefined,
  pageWidth: number,
  pageHeight: number,
): ReactElement {
  const tipIndex = writePageDropCapIndex({
    elements: bodyTexts,
    pageWidth,
  });
  const tipByPairLetter = new Map(
    tipIndex.pairs.map((p) => [p.letterId, p] as const),
  );
  const elementsById = new Map(
    bodyTexts.map((el) => [el.id, toTipElement(el)] as const),
  );
  const tipElements = new Map(bodyTexts.map((el) => [el.id, el] as const));
  const page = { width: pageWidth, height: pageHeight };

  const paragraphGroups = groupNativeLinesIntoParagraphs(bodyTexts);
  const engineGroups = paragraphGroups.filter(isEngineParagraphGroup);
  const engineSkip = new Set<string>();
  for (const group of engineGroups) {
    for (const plate of group) engineSkip.add(plate.id);
  }

  const absoluteSkip = new Set<string>([...engineSkip]);
  const nodes: ReactElement[] = [];

  const legacyPairs = detectNativeDropCapPairs(bodyTexts);

  for (let groupIndex = 0; groupIndex < engineGroups.length; groupIndex += 1) {
    const group = engineGroups[groupIndex]!;
    const tipPair = tipIndex.pairs.find((p) =>
      group.some((plate) => plate.id === p.letterId),
    );

    if (tipPair) {
      const plan = paintDropCapFromTip({
        pair: tipPair,
        elementsById,
        page,
      });
      if (plan) {
        for (const id of tipSkipIds(
          tipPair.letterId,
          tipPair.hostId,
          tipPair.absorbIds,
          tipPair.flowIds,
        )) {
          absoluteSkip.add(id);
        }
        nodes.push(
          <DropCapFromTipFaceObject
            key={`dropcap-tip-${tipPair.letterId}-${tipPair.hostId}`}
            plan={plan}
            tipElements={tipElements}
            assets={assets}
          />,
        );
        continue;
      }
      // Q1 · paint fail → legacy remasure for this group
      const legacy = legacyPairs.find((p) => p.letterId === tipPair.letterId);
      if (legacy) {
        for (const id of nativeDropCapSkipIds([legacy])) absoluteSkip.add(id);
        absoluteSkip.add(legacy.letterId);
        nodes.push(
          <DropCapJustifiedFaceObject
            key={`dropcap-justified-fallback-${legacy.letterId}`}
            pair={legacy}
            pageTexts={bodyTexts}
            paragraphPlates={group}
            assets={assets}
          />,
        );
        continue;
      }
    }

    const legacyDrop = legacyPairs.find((pair) =>
      group.some((plate) => plate.id === pair.letterId),
    );
    // Q1 · tip empty but Face detect still sees pair → keep legacy
    if (legacyDrop && !tipByPairLetter.has(legacyDrop.letterId)) {
      for (const id of nativeDropCapSkipIds([legacyDrop])) absoluteSkip.add(id);
      absoluteSkip.add(legacyDrop.letterId);
      nodes.push(
        <DropCapJustifiedFaceObject
          key={`dropcap-justified-q1-${legacyDrop.letterId}`}
          pair={legacyDrop}
          pageTexts={bodyTexts}
          paragraphPlates={group}
          assets={assets}
        />,
      );
      continue;
    }

    // R10.3a · tipAlign LiveObject rows · no ParagraphJustified remasure
    for (const plate of group) {
      absoluteSkip.add(plate.id);
      nodes.push(
        <LiveObject
          key={`para-tip-${plate.id}`}
          element={plate}
          assets={assets}
          editable={editable}
          {...(onTextChange ? { onTextChange } : {})}
        />,
      );
    }
  }

  // Dropcap pairs outside engine groups (rare) · tip path first
  for (const tipPair of tipIndex.pairs) {
    if (absoluteSkip.has(tipPair.letterId)) continue;
    const plan = paintDropCapFromTip({
      pair: tipPair,
      elementsById,
      page,
    });
    if (!plan) continue;
    for (const id of tipSkipIds(
      tipPair.letterId,
      tipPair.hostId,
      tipPair.absorbIds,
      tipPair.flowIds,
    )) {
      absoluteSkip.add(id);
    }
    nodes.push(
      <DropCapFromTipFaceObject
        key={`dropcap-tip-loose-${tipPair.letterId}`}
        plan={plan}
        tipElements={tipElements}
        assets={assets}
      />,
    );
  }

  for (const element of bodyTexts) {
    if (absoluteSkip.has(element.id)) continue;
    nodes.push(
      <LiveObject
        key={element.id}
        element={element}
        assets={assets}
        editable={editable}
        {...(onTextChange ? { onTextChange } : {})}
      />,
    );
  }

  return <>{nodes}</>;
}

export function renderBodyLayer(
  bodyTexts: readonly TextElement[],
  assets: AssetLibrary,
  editable: boolean,
  onTextChange: BodyLayerOnTextChange | undefined,
  pageWidth: number,
  pageHeight: number,
): ReactElement {
  const pipe = getRenderPipe();
  if (pipe === 'legacy') {
    return renderBodyLayerLegacy(
      bodyTexts,
      assets,
      editable,
      onTextChange,
    );
  }
  if (pipe === 'v2') {
    return renderBodyLayerV2(
      bodyTexts,
      assets,
      editable,
      onTextChange,
      pageWidth,
      pageHeight,
    );
  }
  return renderBodyLayerV1(
    bodyTexts,
    assets,
    editable,
    onTextChange,
    pageWidth,
    pageHeight,
  );
}
